import 'client-only';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConsentToContactApi,
  contract,
  type ClientInferRequest,
} from '@pie/consent-to-contact-sdk';
import { useAuthContext } from '@/components/AuthContext';
import { env } from '@/config/env';
import { ApiError } from '@/lib/api/ApiError';
import type { ConsentStateResponse, SubmitConsentsBody } from './types';

export * from './types';

const { client } = new ConsentToContactApi({ environment: env.NEXT_PUBLIC_PIE_ENV });

const QUERY_KEYS = { CONSENTS: 'consents' } as const;

export const queryKeys = {
  myConsents: () => ['userManagement', QUERY_KEYS.CONSENTS, 'me'] as const,
};

type SessionHeaders = { authorization: string };

type GetMyConsentsSchema = (typeof contract.getMyConsents.responses)[200];
type PostConsentsSchema = (typeof contract.postConsents.responses)[201];
type ConsentStateSchema = GetMyConsentsSchema | PostConsentsSchema;

// Validation is the SDK's schema, except a `source` outside its enum is let
// through: it is provenance only, and a source added server-side before the
// SDK catches up must not suppress the consent prompt (EE-924).
const parseConsentState = (schema: ConsentStateSchema, body: unknown): ConsentStateResponse => {
  const result = schema.safeParse(body);
  if (result.success) return result.data;
  if (result.error.issues.every((issue) => issue.path.at(-1) === 'source')) {
    return body as ConsentStateResponse;
  }
  throw result.error;
};

export const getMyConsents = async (headers: SessionHeaders): Promise<ConsentStateResponse> => {
  const response = await client.getMyConsents({
    extraHeaders: headers,
    overrideClientOptions: { validateResponse: false },
  });

  if (response.status !== 200) {
    throw new ApiError(response.status, response.body, response.headers);
  }

  return parseConsentState(contract.getMyConsents.responses[200], response.body);
};

export const submitConsents = async (
  body: SubmitConsentsBody,
  headers: SessionHeaders,
  idempotencyKey?: string
): Promise<ConsentStateResponse> => {
  const response = await client.postConsents({
    body: body as ClientInferRequest<typeof contract.postConsents>['body'],
    headers: idempotencyKey ? { 'idempotency-key': idempotencyKey } : {},
    extraHeaders: headers,
    overrideClientOptions: { validateResponse: false },
  });

  if (response.status !== 201) {
    throw new ApiError(response.status, response.body, response.headers);
  }

  return parseConsentState(contract.postConsents.responses[201], response.body);
};

export const useMyConsents = ({ enabled = false }: { enabled?: boolean } = {}) => {
  const { getSessionHeaders } = useAuthContext();

  return useQuery({
    enabled,
    queryKey: queryKeys.myConsents(),
    queryFn: async () => {
      const { authorization } = await getSessionHeaders();
      return getMyConsents({ authorization });
    },
    // The modal is suppressed entirely on failure (UI-5), so retrying beyond
    // once just delays that decision without changing the outcome.
    retry: 1,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
};

export const useSubmitConsents = () => {
  const { getSessionHeaders } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      body,
      idempotencyKey,
    }: {
      body: SubmitConsentsBody;
      idempotencyKey?: string;
    }) => {
      const { authorization } = await getSessionHeaders();
      return submitConsents(body, { authorization }, idempotencyKey);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.myConsents(), data);
    },
  });
};
